export const NORMALIZED_UNIT_TONNE = 'TNE';
export const NORMALIZED_UNIT_KILOGRAMM = 'KGM';

const KG_PER_TONNE = 1000;

export function normalizeEinheit(einheit: string | null | undefined): string {
    if (!einheit || typeof einheit !== 'string') {
        return '';
    }
    const upper = einheit.trim().toUpperCase();
    if (upper === 'TNE' || upper === 'T' || upper === 'TONNEN') {
        return NORMALIZED_UNIT_TONNE;
    }
    if (upper === 'KGM' || upper === 'KG' || upper === 'KILOGRAMM') {
        return NORMALIZED_UNIT_KILOGRAMM;
    }
    return upper || '';
}

/**
 * Konvertiert einen Mengenwert in Kilogramm (Basiseinheit für Summen).
 */
export function toKilogramm(menge: number, einheit: string | null | undefined): number {
    if (!Number.isFinite(menge)) {
        return 0;
    }
    const norm = normalizeEinheit(einheit);
    if (norm === NORMALIZED_UNIT_TONNE) {
        return menge * KG_PER_TONNE;
    }
    if (norm === NORMALIZED_UNIT_KILOGRAMM) {
        return menge;
    }
    return menge;
}

/**
 * Liefert true, wenn die Einheit für die Mengenanzeige bekannt ist (TNE oder KGM).
 * Nur dann soll ein Wert in Tonnen umgerechnet und angezeigt werden; sonst leer lassen.
 */
export function isBekannteMengenEinheit(einheit: string | null | undefined): boolean {
    const norm = normalizeEinheit(einheit);
    return norm === NORMALIZED_UNIT_TONNE || norm === NORMALIZED_UNIT_KILOGRAMM;
}

/**
 * Konvertiert einen Mengenwert in Tonnen (für einheitliche Anzeige in der Kontraktübersicht).
 * KGM → geteilt durch 1000, TNE → unverändert.
 */
export function toTonnen(menge: number, einheit: string | null | undefined): number {
    if (!Number.isFinite(menge)) {
        return 0;
    }
    const norm = normalizeEinheit(einheit);
    if (norm === NORMALIZED_UNIT_TONNE) {
        return menge;
    }
    if (norm === NORMALIZED_UNIT_KILOGRAMM) {
        return menge / KG_PER_TONNE;
    }
    return menge;
}

/**
 * Konvertiert Kilogramm zurück in die gewünschte Anzeige-Einheit.
 * targetEinheit: kanonischer Code (TNE oder KGM) oder leer (dann kg).
 */
export function fromKilogramm(mengeKg: number, targetEinheit: string): { wert: number; einheit: string } {
    if (!Number.isFinite(mengeKg)) {
        return { wert: 0, einheit: targetEinheit || NORMALIZED_UNIT_KILOGRAMM };
    }
    const norm = normalizeEinheit(targetEinheit);
    if (norm === NORMALIZED_UNIT_TONNE) {
        return { wert: mengeKg / KG_PER_TONNE, einheit: NORMALIZED_UNIT_TONNE };
    }
    return { wert: mengeKg, einheit: NORMALIZED_UNIT_KILOGRAMM };
}

export function summeNachEinheit(items: Array<{ menge: number; einheit: string | null | undefined }>): {
    summeKg: number;
    einheitlicheEinheit: string | null;
    alleEinheiten: Set<string>;
} {
    const alleEinheiten = new Set<string>();
    let summeKg = 0;

    for (const { menge, einheit } of items) {
        const norm = normalizeEinheit(einheit);
        if (norm) {
            alleEinheiten.add(norm);
        }
        summeKg += toKilogramm(menge, einheit);
    }

    const einheitlicheEinheit = alleEinheiten.size === 1 ? (Array.from(alleEinheiten)[0] as string) : null;

    return { summeKg, einheitlicheEinheit, alleEinheiten };
}
